/* ============================================
   Cerrar una oportunidad — ¿se hizo la venta o no?

   `etapa='cerrado'` era el final del tablero y nada más: la oportunidad que
   se firmó y la que el cliente rechazó terminaban en la misma columna. Así,
   el pipeline no podía responder «cuánto cerré este mes» ni «por qué se me
   cae la gente», que es lo único que se le pregunta a un pipeline cerrado.

   El modal aparece en los dos caminos que llevan a Cerrado —arrastrar la
   tarjeta y los botones de etapa de la ficha— porque un camino sin la
   pregunta es un lead cerrado sin resultado, y el reporte vuelve a mentir.

   **Y es el momento en que el partner nos debe.** Cuando la cotización lleva
   servicios nuestros, marcar ganado genera la factura nuestra hacia él: es el
   único instante en que alguien afirma que la venta ocurrió. El modal lo dice
   antes de confirmar —con el importe— en vez de que aparezca una factura sin
   explicación en Mis facturas.
   ============================================ */
import { useEffect, useMemo, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import type { Cotizacion, CotizacionItemsEstandar, Lead, ResultadoLead } from '@/lib/types'
import { formatCurrency, r2 } from '@/lib/utils'
import { IconCheckCircle, IconXCircle } from '@/components/icons/Icons'

/* Los motivos salen de lo que se pierde de verdad en una agencia, no de un
   catálogo genérico: son los que después se pueden contar. «Otro» existe
   porque forzar un motivo falso es peor que no tenerlo, y abre la nota. */
const MOTIVOS = [
  'Por precio',
  'Eligió a otro proveedor',
  'No era el momento',
  'Dejó de responder',
  'Lo hace internamente',
  'Otro',
] as const

interface Props {
  lead: Lead
  onCancelar: () => void
  /** Cerrado y guardado. El padre refresca y avisa; el modal no sabe de toasts. */
  onCerrado: (res: {
    resultado: ResultadoLead
    facturas: Array<{ numero: string; total: number; moneda: string }>
    avisos: string[]
  }) => void
}

/** Lo que nos deben de una cotización, por los ítems que salieron de nuestro catálogo. */
function costoDe(cot: Cotizacion): { monto: number; moneda: string } {
  const items = Array.isArray(cot.items) ? (cot.items as CotizacionItemsEstandar[]) : []
  const conCosto = items.filter(it => Number(it?.costo_partner) > 0)
  if (conCosto.length === 0) return { monto: 0, moneda: cot.moneda }
  return {
    monto: r2(conCosto.reduce((a, it) => a + Number(it.costo_partner) * (Number(it?.cantidad) || 0), 0)),
    moneda: conCosto[0].costo_partner_moneda || cot.moneda,
  }
}

/** Resultado de un cierre, tal como lo devuelve el modal. */
export interface CierreHecho {
  resultado: ResultadoLead
  facturas: Array<{ numero: string; total: number; moneda: string }>
  avisos: string[]
}

/*
  Lo que se le dice al usuario después de cerrar. Vive acá y no en cada
  pantalla porque el cierre se dispara desde dos lugares (el tablero y la
  ficha) y el aviso tiene que decir lo mismo en los dos: si se generó una
  factura, su número — sin eso, aparece un documento en Mis facturas que
  nadie recuerda haber pedido.
*/
export function mensajeDeCierre(res: CierreHecho): string {
  if (res.resultado === 'perdido') return 'Oportunidad cerrada como perdida'
  if (res.facturas.length > 0) {
    const numeros = res.facturas.map(f => f.numero).join(', ')
    return `Cerrada como ganada. Se generó la factura ${numeros} en borrador.`
  }
  return 'Cerrada como ganada'
}

export default function CierreLeadModal({ lead, onCancelar, onCerrado }: Props) {
  const api = useApi()
  const { isPartner } = useAuth()

  const [eleccion, setEleccion] = useState<ResultadoLead | null>(null)
  const [motivo, setMotivo] = useState('')
  const [nota, setNota] = useState('')
  const [cotizaciones, setCotizaciones] = useState<Cotizacion[]>([])
  const [aceptarPendientes, setAceptarPendientes] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  /* Las cotizaciones de ESTA oportunidad: de ellas sale lo que nos deben, y
     sin ellas el modal no puede decir qué se va a facturar. Si la consulta
     falla se cierra igual: el resultado del lead no depende de un importe. */
  useEffect(() => {
    let vivo = true
    api.get<Cotizacion[]>(`/api/cotizaciones?lead_id=${lead.id}`)
      .then(r => { if (vivo) setCotizaciones(Array.isArray(r) ? r : []) })
      .catch(() => { /* sin importe que mostrar */ })
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id])

  /*
    Las que nos generan deuda. Una rechazada queda afuera: el cliente dijo que
    no a ESE documento, y si la venta se hizo fue con otro.
  */
  const conCosto = useMemo(
    () => cotizaciones
      .filter(c => c.estado !== 'rechazada' && costoDe(c).monto > 0)
      .map(c => ({ cot: c, ...costoDe(c) })),
    [cotizaciones],
  )
  const yaAceptadas = conCosto.filter(x => x.cot.estado === 'aceptada')
  const porAceptar = conCosto.filter(x => x.cot.estado !== 'aceptada')

  /*
    El importe que se va a facturar, por moneda. Nunca una suma sola: los
    costos de una misma cartera pueden estar en soles y en dólares, y un
    total mezclado es un número que no existe.
  */
  const aFacturar = useMemo(() => {
    const incluidas = [...yaAceptadas, ...(aceptarPendientes ? porAceptar : [])]
    const porMoneda = new Map<string, number>()
    for (const x of incluidas) porMoneda.set(x.moneda, r2((porMoneda.get(x.moneda) || 0) + x.monto))
    return [...porMoneda.entries()].map(([moneda, monto]) => ({ moneda, monto }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conCosto, aceptarPendientes])

  async function confirmar() {
    if (!eleccion) return
    setGuardando(true)
    setError('')
    try {
      /*
        Las cotizaciones primero: el backend factura sobre las ACEPTADAS, así
        que marcarlas después dejaría el cierre sin factura y obligaría a
        generarla a mano.
      */
      if (eleccion === 'ganado' && aceptarPendientes) {
        for (const x of porAceptar) {
          await api.put(`/api/cotizaciones/${x.cot.id}`, { estado: 'aceptada' })
        }
      }

      const motivoFinal = eleccion === 'perdido'
        ? [motivo, nota.trim()].filter(Boolean).join(' — ') || null
        : null

      const res = await api.put<Record<string, unknown>>(`/api/leads/${lead.id}`, {
        etapa: 'cerrado',
        resultado: eleccion,
        motivo_perdida: motivoFinal,
      })

      onCerrado({
        resultado: eleccion,
        facturas: Array.isArray(res?.facturas_partner) ? res.facturas_partner as never[] : [],
        avisos: Array.isArray(res?.facturas_partner_avisos) ? res.facturas_partner_avisos as string[] : [],
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cerrar la oportunidad')
      setGuardando(false)
    }
  }

  const nombre = lead.empresa || lead.titulo || lead.codigo

  return (
    <div
      className="ld-modal-fondo"
      role="dialog"
      aria-modal="true"
      aria-label="Cerrar la oportunidad"
      onClick={e => { if (e.target === e.currentTarget && !guardando) onCancelar() }}
    >
      <div className="ld-modal cierre-modal">
        <h3 className="ld-modal__titulo">¿Se hizo la venta?</h3>
        <p className="ld-modal__texto">
          Estás cerrando <strong>{nombre}</strong>. Lo que elijas acá es lo que después
          responde cuánto cerraste y por qué se cae lo que se cae.
        </p>

        <div className="cierre-opciones">
          <button
            type="button"
            className={`cierre-opcion cierre-opcion--ganado${eleccion === 'ganado' ? ' is-elegida' : ''}`}
            onClick={() => setEleccion('ganado')}
            disabled={guardando}
          >
            <IconCheckCircle size={20} />
            <strong>Ganado</strong>
            <span>El cliente aceptó</span>
          </button>
          <button
            type="button"
            className={`cierre-opcion cierre-opcion--perdido${eleccion === 'perdido' ? ' is-elegida' : ''}`}
            onClick={() => setEleccion('perdido')}
            disabled={guardando}
          >
            <IconXCircle size={20} />
            <strong>Perdido</strong>
            <span>No se concretó</span>
          </button>
        </div>

        {/* ─── Ganado: qué se registra ─── */}
        {eleccion === 'ganado' && (
          <div className="cierre-detalle">
            {conCosto.length === 0 ? (
              <p className="cierre-detalle__nota">
                Queda registrada como ganada. {isPartner
                  ? 'No hay nada que nos debas por esta oportunidad.'
                  : 'Sus cotizaciones no tienen costos de partner cargados.'}
              </p>
            ) : (
              <>
                {porAceptar.length > 0 && (
                  <label className="cierre-check">
                    <input
                      type="checkbox"
                      checked={aceptarPendientes}
                      onChange={e => setAceptarPendientes(e.target.checked)}
                      disabled={guardando}
                    />
                    <span>
                      Marcar como aceptada{porAceptar.length > 1 ? 's' : ''} la cotización
                      {porAceptar.length > 1 ? 'es' : ''} {porAceptar.map(x => x.cot.numero).join(', ')}
                    </span>
                  </label>
                )}
                {aFacturar.length > 0 ? (
                  <div className="cierre-importe">
                    <span>{isPartner ? 'Nos pagas' : 'Se le factura al partner'}</span>
                    <strong>{aFacturar.map(x => formatCurrency(x.monto, x.moneda)).join(' + ')}</strong>
                    <small>
                      {isPartner
                        ? 'La factura te queda en Mis facturas, en borrador: la revisamos antes de emitirla.'
                        : 'Se genera en borrador, para revisarla antes de emitir.'}
                    </small>
                  </div>
                ) : (
                  <p className="cierre-detalle__nota">
                    Sin ninguna cotización aceptada no se genera factura. Marca la casilla de
                    arriba, o acepta el documento desde la cotización.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* ─── Perdido: por qué ─── */}
        {eleccion === 'perdido' && (
          <div className="cierre-detalle">
            <div className="cierre-motivos">
              {MOTIVOS.map(m => (
                <button
                  key={m}
                  type="button"
                  className={`cierre-motivo${motivo === m ? ' is-elegido' : ''}`}
                  onClick={() => setMotivo(motivo === m ? '' : m)}
                  disabled={guardando}
                >
                  {m}
                </button>
              ))}
            </div>
            <textarea
              className="form-input ld-modal__nota"
              value={nota}
              onChange={e => setNota(e.target.value)}
              placeholder="Qué pasó, en tus palabras (opcional)"
              rows={3}
              disabled={guardando}
            />
          </div>
        )}

        {error && <p className="ld-tareas__error">{error}</p>}

        <div className="ld-modal__acciones">
          <button type="button" className="btn btn-secondary" onClick={onCancelar} disabled={guardando}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirmar}
            disabled={!eleccion || guardando}
          >
            {guardando
              ? 'Guardando...'
              : eleccion === 'perdido' ? 'Cerrar como perdido' : 'Cerrar como ganado'}
          </button>
        </div>
      </div>
    </div>
  )
}
